import { usePage } from '@inertiajs/vue3'
import { computed, useAttrs, type ComputedRef } from 'vue'
import type { PageProps } from '@/Models/Inertia'
import type { FieldError, FieldLayout, FormFieldBaseProps } from './types'

/**
 * Rood kader bij een fout. Letterlijk overgenomen uit VInput zodat bestaande schermen
 * na migratie pixel-identiek blijven.
 */
export const INVALID_CONTROL_CLASSES = '!border-[#e6614f] !bg-[#fdf0ee]'

/**
 * Klassen op de veldwortel. De grid is die van `.edit-create .v-input` (de gekozen
 * referentielook); zie _v-field.scss voor de rest van de look.
 */
export function fieldLayoutClasses(layout: FieldLayout | undefined): string {
    return layout === 'stacked'
        ? 'v-field v-field--stacked'
        : // content-start: wordt het veld uitgerekt (bv. naast een hoger veld in een grid),
          // dan blijven label/veld/fout compact i.p.v. de extra hoogte te verdelen.
          'v-field v-field--horizontal sm:grid sm:grid-cols-3 sm:items-start sm:content-start sm:gap-x-4'
}

/**
 * Grid van de leesweergave (VDisplayValue). Letterlijk die van VInput's
 * `display-only-value`: daar grepen geen contextregels op aan, dus die was al overal
 * gelijk.
 */
export function displayLayoutClasses(layout: FieldLayout | undefined): string {
    return layout === 'stacked' ? '' : 'items-baseline lg:grid lg:grid-cols-3'
}

/**
 * Splitst `$attrs`: `class` en `style` gaan naar de wrapper, al de rest (ook listeners)
 * naar het control-element.
 *
 * Waarom: VInput had geen `inheritAttrs: false` en bond `$attrs` daarnaast ook op de
 * input. Elke attribuut en listener stond daardoor twee keer in de DOM: een `@change`
 * of `@keyup` op een tekstveld vuurde op de input én (via bubbling) op de wrapper-div.
 * Componenten die dit gebruiken moeten `defineOptions({ inheritAttrs: false })` zetten.
 */
export function useSplitAttrs(): {
    rootAttrs: ComputedRef<Record<string, unknown>>
    controlAttrs: ComputedRef<Record<string, unknown>>
} {
    const attrs = useAttrs()

    const rootAttrs = computed(() => {
        const result: Record<string, unknown> = {}

        if (attrs.class !== undefined) {
            result.class = attrs.class
        }
        if (attrs.style !== undefined) {
            result.style = attrs.style
        }
        // Zoekanker van de globale zoekfunctie (GH #722): hoort op de hele veldrij
        // (label + veld) zodat scrollen en de highlight-ring het label mee omvatten,
        // niet enkel het invoervak.
        if (attrs['data-search-anchor'] !== undefined) {
            result['data-search-anchor'] = attrs['data-search-anchor']
        }

        return result
    })

    const controlAttrs = computed(() => {
        const { class: _class, style: _style, 'data-search-anchor': _searchAnchor, ...rest } = attrs

        return rest
    })

    return { rootAttrs, controlAttrs }
}

function normalizeError(error: FieldError): string[] {
    if (error === null || error === undefined || error === '') {
        return []
    }

    return (Array.isArray(error) ? error : [error]).filter((message) => message !== '')
}

/**
 * Foutmeldingen voor één veld.
 *
 * Een expliciete `error`-prop wint; zonder die prop vallen we terug op de Inertia
 * `page.props.errors[name]`, exact zoals VInput dat deed.
 */
export function useFieldErrors(
    props: Pick<FormFieldBaseProps, 'name' | 'error' | 'showErrors'>
): ComputedRef<string[]> {
    const page = usePage<PageProps>()

    return computed(() => {
        if (props.showErrors === false) {
            return []
        }

        if (props.error !== undefined) {
            return normalizeError(props.error)
        }

        if (!props.name) {
            return []
        }

        const allErrors = (page.props.errors ?? {}) as Record<string, string>

        return normalizeError(allErrors[props.name])
    })
}

/**
 * Klassen op het control-element, identiek aan VInput's `generatedInputClasses`.
 *
 * `inputClasses` staat bewust zowel op de control-container als op het control zelf:
 * zo deed VInput het, en bestaande schermen rekenen daarop (bv. `text-right`).
 */
export function controlClasses(options: {
    inputClasses?: string
    invalid?: boolean
    hasPrefix: boolean
    hasSuffix: boolean
    fullWidth?: boolean
}): string {
    let classes = `${options.inputClasses ?? ''} `

    if (options.fullWidth !== false) {
        classes += 'w-full '
    }
    if (options.invalid) {
        classes += `${INVALID_CONTROL_CLASSES} `
    }

    if (!options.hasPrefix && !options.hasSuffix) {
        classes += 'rounded'
    } else if (options.hasPrefix) {
        classes += 'rounded-r'
    } else {
        classes += 'rounded-l'
    }

    return classes
}

/**
 * De props die elk veld 1-op-1 doorgeeft aan VFormField.
 */
export function useFormFieldBindings(
    props: FormFieldBaseProps,
    errors: ComputedRef<string[]>
): ComputedRef<Record<string, unknown>> {
    return computed(() => ({
        label: props.label,
        for: props.id ?? props.name,
        required: props.required,
        example: props.example,
        info: props.info,
        errors: errors.value,
        layout: props.layout,
        indent: props.indent,
        hasPrefixSelect: props.hasPrefixSelect,
        labelClasses: props.labelClasses,
        inputClasses: props.inputClasses,
        exampleClasses: props.exampleClasses,
        errorClasses: props.errorClasses,
        infoClasses: props.infoClasses,
        suffixClasses: props.suffixClasses,
    }))
}
